<template>
  <div class="message" :class="{ tooltipActive, isPinned, isAdmin, isNotification, isReply: reply }">
    <div class="sidebar">
      <div @click="openContactCard">
        <Avatar :name="sender.name" :image="sender.avatar" />
      </div>
    </div>
    <div class="main">
      <div class="name">
        <a ref="text" @click="openContactCard">{{sender.name}} <VerifiedIcon v-if="sender.sponsor" /></a>
        <div 
          :class="{ 'fixed': tooltipFixed }"
          ref="tooltip"
          tabindex="-1"
          class="tooltip"
          @blur="tooltipBlur"
          :style="{ top: tooltipStyleTop }"
        >
          <UserCard :user="sender" />
        </div>
        <div class="overlay"></div>
        <span class="time">{{formattedTime}}</span>
        <div class="actions" v-if="!disabled">
          <button 
            aria-label="React with emoji"
            v-for="emoji in visualSettings.messageQuickReactions" :key="emoji"
            class="icon reaction"
            type="button"
            @click="react({ emoji })"
          >
            {{emoji}}
          </button>
          <button 
            v-if="isAdmin && !reply && activeChannel.type === 'group'"
            class="icon"
            aria-label="Delete"
            type="button"
            @click="showDeleteModal"
          >
            <fa-icon
              :icon="['fas', 'trash']"
              size="sm"
            />
          </button>
          <button
            v-if="!reply"
            class="icon reply"
            aria-label="Reply"
            type="button"
            @click="openReply"
          >
            <fa-icon
              :icon="['fas', 'comments']"
              size="sm"
            />
          </button>
          <button
            class="icon pin"
            aria-label="Pin"
            type="button"
            @click="pin"
          >
            <fa-icon
              :icon="['fas', 'thumbtack']"
              size="sm"
            />
          </button>
        </div>
      </div>
      <p v-if="text" v-html="text" v-linkified:options="linkOptions"></p>
      <button 
        v-if="isCall && sender.uuid !== activeUser.uuid"
        class="call"
        type="button"
        @click="answerCall"
      >
        Answer Call
      </button>
      <div
        v-if="attachment"
        class="attachment" :style="{ backgroundImage: `url('${attachment}')` }"
        @click="showImageZoomModal"
      >
        <img ref="attachment" :src="attachment" />
      </div>
      <div>
        <div 
          class="reaction-count"
          :class="{ reacted: reaction.reacted }"
          v-for="(reaction, emoji) in sortedReactions"
          :key="emoji"
          @click="react({ emoji, id: reaction.id })"
        >
          {{emoji}} {{reaction.value}}
        </div>
        <div 
          class="reaction-count"
          v-if="replies.length && !disabled && !reply"
          @click="openReply"
        >
          {{`${replies.length} ${replies.length === 1 ? 'Reply' : 'Replies'}`}}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar';
import UserCard from '@/components/UserCard';
import VerifiedIcon from '@/components/VerifiedIcon';
import DeleteMessage from '@/components/modals/DeleteMessage';
import ImageZoom from '@/components/modals/ImageZoom';

import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations
} from 'vuex';

export default {
  name: 'Message',
  components: {
    Avatar,
    UserCard,
    VerifiedIcon
  }, 
  props: [
    'sender',
    'pinned',
    'text',
    'attachment',
    'time',
    'channelId',
    'isNotification',
    'isCall',
    'reply',
    'disabled',
    'showTime'
  ],
  data() {
    return {
      tooltipActive: false,
      tooltipFixed: false,
      tooltipStyleTop: 0,
      linkOptions: {
        format: {
          url: function (value) {
            return value.length > 50 ? value.slice(0, 50) + '…' : value
          }
        }
      }
    }
  },
  computed: {
    replies() {
      const actionIds = [];
      const actions = [];
      this.messageActionsById({
        channelId: this.channelId,
        messageId: this.time
      }).forEach((action) => {
        if (!actionIds.includes(action.actionId)) {
          actions.push(action);
          actionIds.push(action.actionId);
        }
      });
      return actions.filter((action) => (action.type === 'reply'));
    },
    sortedReactions() {
      const reactions = {};
      this.messageActionsById({
        channelId: this.channelId,
        messageId: this.time
      }).forEach((action) => {
        if (action.type !== 'react') {
          return;
        }
        if (!reactions[action.value]) {
          reactions[action.value] = {
            value: 0,
            reacted: false,
            id: null
          };
        }

        if (action.userUuid === this.activeUser.uuid) {
          reactions[action.value].reacted = true;
          reactions[action.value].id = action.actionId;
        }

        reactions[action.value].value++;
      });
      return reactions;
    },
    formattedTime() {
      if (this.showTime || this.reply) {
        const date = this.$moment(this.$moment(this.time/10000).format('YYYY-MM-DD'));
        let formatted = '';
        switch(this.diffDays(date)) {
          case 0:
            formatted = 'Today';
            break;
          case 1:
            formatted = 'Yesterday';
            break;
          default:
            formatted = date.format('dddd, MMMM Do');
        }

        return `${formatted}, ${this.$moment(this.time/10000).format('h:mm A')}`;
      }

      return this.$moment(this.time/10000).format('h:mm A');
    },
    isPinned() {
      return this.messageActionsById({
        channelId: this.channelId,
        messageId: this.time
      }).find((action) => action.type === 'pin');
    },
    ...mapGetters([
      'visualSettings',
      'messageActionsById',
      'hasVideoOrAudioInput'
    ]),
    ...mapState([
      'demoMode',
      'isAdmin',
      'activeUser',
      'activeChannel'
    ])
  },
  methods: {
    ...mapMutations([
      'setActiveMessageId'
    ]),
    ...mapActions([
      'initCall',
      'loadMediaDevices',
      'createAction',
      'removeAction',
      'createAdminAction',
      'removeAdminAction'
    ]),
    diffDays(date) {
      return this.$moment(new Date(),'HH').diff(date, 'days');
    },
    async openReply() {
      this.setActiveMessageId(this.time);
    },
    async react({ emoji, id }) {
      if (this.demoMode) {
        return;
      }
      !id && await this.createAction({
        messageId: this.time,
        channelId: this.channelId,
        type: 'react',
        value: emoji
      });
      id && await this.removeAction({
        messageId: this.time,
        channelId: this.channelId,
        actionId: id
      });
    },
    async answerCall() {
      await this.loadMediaDevices();
      if (this.hasVideoOrAudioInput) {
        this.initCall({});
      } else {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Audio/Video Not Found',
          text: 'Unable to find audio or video devices to begin call. Please check your browser settings.',
          duration: 5000
        });
      }
    },
    async pin() {
      try {
        if (!this.isAdmin) {
          return;
        }
        if (!this.isPinned) {
          await this.createAdminAction({
            messageId: this.time,
            channelId: this.channelId,
            type: 'pin',
            value: this.text
          });
          this.$notify({
            group: 'app',
            type: 'success',
            title: 'Message Pinning',
            text: 'The message has been successfully pinned.'
          });
        } else if (this.isPinned) { 
          await this.removeAdminAction({
            messageId: this.time,
            channelId: this.channelId,
            actionId: this.isPinned.actionId
          });
          this.$notify({
            group: 'app',
            type: 'success',
            title: 'Message Pinning',
            text: 'The message has been successfully unpinned.'
          });
        }
      } catch (e) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Message Pinning',
          text: 'Unable to change pin.'
        });
      }
    },
    showDeleteModal() {
      this.$modal.show(
        DeleteMessage,
        { 
          messageId: this.time,
          channelId: this.channelId 
        },
        {
          width: '80%',
          maxWidth: 400,
          height: 'auto',
          adaptive: true
        }
      );
    },
    showImageZoomModal() {
      const width = this.$refs.attachment.naturalWidth;

      this.$modal.show(
        ImageZoom,
        { 
          image: this.attachment
        },
        {
          width: '80%',
          minWidth: 100,
          maxWidth: width < 800 ? width : 800,
          height: 'auto',
          adaptive: true,
          scrollable: true
        }
      );
    },
    tooltipBlur() {
      this.tooltipActive = false;
      this.disableResizeListener();
    },
    openContactCard(e) {
      e.preventDefault();
      this.positionCard();
      this.$refs.tooltip.focus();
      this.tooltipActive = true; 
      this.enableResizeListener();
    },
    handleResize() {
      this.positionCard();
    },
    enableResizeListener() {
      window.addEventListener('resize', this.handleResize);
    },
    disableResizeListener() {
      window.removeEventListener('resize', this.handleResize);
    },
    positionCard() {
      const buffer = 67;
      const browserHeight = window.innerHeight;
      const browserWidth = window.innerWidth;
      const tooltipHeight = this.$refs.tooltip.clientHeight;
      const tooltipWidth = this.$refs.tooltip.clientWidth;
      const textWidth = this.$refs.text.getBoundingClientRect().width;
      const textTop = this.$refs.text.getBoundingClientRect().top - 25;
      const textLeft = this.$refs.text.getBoundingClientRect().left + 36;

      if(textTop + tooltipHeight > browserHeight - buffer) {
        this.tooltipStyleTop = `${browserHeight - tooltipHeight - buffer}px`;
      } else if(textTop < buffer) {
        this.tooltipStyleTop = `${buffer}px`;
      } else {
        this.tooltipStyleTop = `${textTop}px`;
      }

      this.tooltipFixed = (textLeft + textWidth + tooltipWidth > browserWidth);
    }
  }
}
</script>

<style scoped lang="scss">
.message {
  display: table;
  width: 100%;
  padding: 10px 12px 12px;
  border-radius: 8px;
  box-sizing: border-box;
  margin: 2px 0;
  transition: background 250ms ease-out;

  @media only screen and (max-width: 768px) {
    padding-left: 10px;
    padding-right: 10px;
  }

  &:hover {
    background: rgba(0,0,0,0.05);

    .actions {

      button {
        opacity: 1;
      }
    }
  }

  &.isPinned {
    background: rgba(0,0,0,0.05);

    .actions {

      button.pin {
        opacity: 1 !important;
        display: inline-block !important;
        color: rgba(47, 49, 56, 1) !important;
        cursor: default !important;

        &:hover {
          background: none !important;
        }
      }
    }
  }

  &.tooltipActive {

    &:hover {
      background: none;

      .actions {
        opacity: 0;
      }
    }
  }

  &.isAdmin {

    .actions {
      
      button {
        display: inline-block !important;
        cursor: pointer !important;

        &:hover {
          background: rgba(234, 235, 239, 0.5) !important;
        }
      }
    }
  }

  &.isNotification {
    color: rgba(0,0,0,0.5);
    font-size: 14px;

    .actions {
      display: none;
    }
  }
  
  &.isReply {
    background: none;
    
    &:hover {
      background: none;
    }

    .actions {

      @media only screen and (max-width: 768px) {
        position: static;
        top: auto;
        text-align: right;
      }

      button {

        &.reaction {
          opacity: 1;
        }
      }
    }
  }
}

.sidebar {
  width: 1px;
  display: table-cell;

  > * {
    cursor: pointer;
  }
}

.main {
  padding: 10px 0 0 12px;
  display: table-cell;
  vertical-align: top;
  font-size: 0; 
}

.name {
  position: relative;
  font-size: 12.8px;
  line-height: 1;
  color: rgba(47, 49, 56, 1);
  margin: 0;
  font-weight: bold;

  a {
    color: rgba(47, 49, 56, 1);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
      opacity: 1;
    }
  }
}

.actions {
  position: absolute;
  top: -9px;
  right: 0;

  button {
    display: none;
    opacity: 0;

    &:hover {
      opacity: 1;
    }

    &.reaction {
      display: inline-block;
      font-size: 13.7px;
    }

    &.reply {
      display: inline-block;
    }
  }
}

button.call {
  height: 30px;
  border-radius: 4px;
  font-size: 14.7px;
  border: 0;
  padding: 0;
  background: #2bbd7e;
  color: #fff;
  cursor: pointer;
  margin: 4px 0 0 0;
  padding: 0 8px;
  transition: background 250ms ease-out,
    color 250ms ease-out,
    opacity 250ms ease-out;
}

button.icon {
  width: 30px;
  height: 30px;
  border-radius: 4px;
  font-size: 14.7px;
  border: 0;
  padding: 0;
  background: none;
  color: rgba(47, 49, 56, 0.6);
  cursor: pointer;
  transition: background 250ms ease-out,
    color 250ms ease-out,
    opacity 250ms ease-out;

  &:focus {
    outline: none;
    box-shadow: none;
    background: none;
    color: rgba(47, 49, 56, 0.6);
  }

  &.selected,
  &:hover {
    background: rgba(234, 235, 239, 0.5);
    color: rgb(47, 49, 56);
  }
}

.tooltip {
  position: fixed;
  display: inline-block;
  top: 0;
  left: -10000px;
  background: #fff;
  padding: 16px;
  border-radius: 5px;
  box-shadow: 0 4px 16px rgba(60, 40, 40, 0.12);
  margin: 0 0 0 8px;
  opacity: 0;
  overflow: hidden;
  box-sizing: border-box;
  width: 300px;
  max-width: calc(100% - 32px);
  z-index: 3;
  transition: opacity 750ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
  
  &.fixed {
    right: 36px;
  }

  &:focus {
    opacity: 1;
    left: auto;
    display: inline-block;
    outline: none;

    @media only screen and (max-width: 768px) {
      right: 16px !important;
    }

    &+ .overlay {
      display: block;
    }
  }
}

.overlay {
  position: fixed;
  top: 60px;
  left: 250px;
  right: 0;
  bottom: 60px;
  display: none;

  @media only screen and (max-width: 768px) {
    left: 0;
  }
}

.time {
  color: rgba(47, 49, 56, 0.35);
  padding: 0 0 0 8px;
  font-weight: normal;
}

p {
  margin: 8px 0 0 0;
  font-size: 16px;
  word-break: break-word;

  ::v-deep a {
    word-break: break-all;
  }
}

.reaction-count {
  box-sizing: border-box;
  display: inline-block;
  font-size: 10px;
  line-height: 1.5;
  border-radius: 4px;
  border: 1px solid rgba(0,0,0,.07);
  margin: 2px 0 0 4px;
  padding: 3px 5px;
  cursor: pointer;
  transition: background 250ms ease-out,
    border-color 250ms ease-out;

  &.reacted {
    border-color: rgba(0,0,0,.25);
  }

  &:hover {
    background: #fff;
    border-color: transparent;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  &:first-child {
    margin-left: 0;
  }
}

.attachment {
  position: relative;
  margin: 8px 0 1px 0;
  width: 100px;
  height: 100px;
  max-width: 100%;
  border-radius: 4px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;
  cursor: zoom-in;
  transition: box-shadow 250ms ease-out;

  img {
    width: 0;
    height: 0;
  }

  &:hover {
    box-shadow: 0 4px 16px rgba(60, 40, 40, 0.12);
  }
}
</style>
